import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface ProjectProfitabilityRow {
  project_id: string;
  number: string;
  name: string;
  entity_name: string;
  billing_type: string;
  start_date: string;
  due_date: string;
  budget_hours: number;
  budget_amount: number;
  actual_hours: number;
  revenue: number;
  cost: number;
  margin: number;
  margin_percent: number | null;
}

@Component({
  selector: 'app-project-profitability-report',
  templateUrl: './project-profitability-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class ProjectProfitabilityReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Project Profitability Report';
  isLoading = false;
  noDataFound = false;
  rows: ProjectProfitabilityRow[] = [];
  entities: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    entity_id: '',
    from_date: '',
    to_date: '',
  };

  vm: any = {
    Preferences: [],
  };

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('entity', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.entities = result.Data ?? result.items ?? [];
      },
    });
  }

  get grandRevenue(): number {
    return this.rows.reduce((s, r) => s + r.revenue, 0);
  }

  get grandCost(): number {
    return this.rows.reduce((s, r) => s + r.cost, 0);
  }

  get grandMargin(): number {
    return this.rows.reduce((s, r) => s + r.margin, 0);
  }

  get grandMarginPct(): number | null {
    const rev = this.grandRevenue;
    return rev > 0 ? (this.grandMargin / rev) * 100 : null;
  }

  marginClass(pct: number | null): string {
    if (pct == null) {
      return '';
    }
    if (pct > 20) {
      return 'text-success';
    }
    if (pct >= 0) {
      return 'text-warning';
    }
    return 'text-danger';
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      entity_id: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.vm.report = [];
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];

    const params: string[] = [];
    if (this.filters.company_id) {
      params.push('company_id=' + this.filters.company_id);
    }
    if (this.filters.entity_id) {
      params.push('entity_id=' + this.filters.entity_id);
    }
    const fromDate = this.filters.from_date ? this.dateFilter.GetDateFormat(this.filters.from_date) : '';
    const toDate = this.filters.to_date ? this.dateFilter.GetDateFormat(this.filters.to_date) : '';
    if (fromDate) {
      params.push('from_date=' + fromDate);
    }
    if (toDate) {
      params.push('to_date=' + toDate);
    }

    this.tableDataService.getDataWithParams('ProjectProfitabilityReport', params.join('&')).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : [];
        this.vm.report = this.rows;
        this.noDataFound = this.rows.length === 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('Project Profitability Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `ProjectProfitability_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    if (this.filters.from_date || this.filters.to_date) {
      meta.push({label: 'Date Range', value: [this.filters.from_date, this.filters.to_date].filter(Boolean).join(' to ')});
    }
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
